import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Button } from "@asosteam/asos-web-component-library-button";
import { TextField } from "../../components/text-field";
import { useWithdrawalFormDetails } from "../../context/withdrawal-details";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";
import { isValidEmailAddress } from "../../utilities/validators";
import { postToEventGrid } from "../../utilities/post-to-event-grid";
import { replaceJSX } from "../../utilities/utilities";
import {
  setFirstName,
  setLastName,
  setEmailAddress,
  setOrderNumber,
  setOrderDate,
  setDeliveredOn,
  CONTRACT_TYPES,
} from "../../state/withdrawal-form-details/reducer";
import styles from "./styles";

const LINKS = {
  TCS: "/terms-and-conditions/",
  RETURNS: "/customer-care/returns-refunds/",
  PRIVACY_NOTICE: "/privacy-policy/",
};

const ORDER_NUMBER_DESCRIPTION_KEYS = {
  [CONTRACT_TYPES.ORDERS]: "contract_withdrawal_form_order_number_description",
  [CONTRACT_TYPES.PREMIER]:
    "contract_withdrawal_form_order_number_recent_description",
  [CONTRACT_TYPES.LOYALTY]:
    "contract_withdrawal_form_order_number_recent_description",
};

const CONTRACT_TYPES_WITH_ORDER_NUMBER = new Set([
  CONTRACT_TYPES.ORDERS,
  CONTRACT_TYPES.PREMIER,
  CONTRACT_TYPES.LOYALTY,
]);

export default function WithdrawalForm({ setFormStatus }) {
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  const localeProvider = useLocaleProvider();
  const translate = useTranslate(localeProvider);
  const links = { ...LINKS, ...localeProvider?.localisedLinks };
  const [state, dispatch] = useWithdrawalFormDetails();
  const {
    contractType = "",
    firstName = "",
    lastName = "",
    email = "",
    orderNumber = "",
    orderDate = "",
    deliveredOn = "",
  } = state.withdrawalFormDetails;

  const [validationErrors, setValidationErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    orderNumber: false,
    orderDate: false,
    deliveredOn: false,
  });

  useEffect(() => {
    setHasErrors(Object.values(validationErrors).some(Boolean));
  }, [validationErrors]);

  const isOrdersType = contractType === CONTRACT_TYPES.ORDERS;
  const showsOrderNumber = CONTRACT_TYPES_WITH_ORDER_NUMBER.has(contractType);

  useEffect(() => {
    setValidationErrors((prev) => ({
      ...prev,
      orderDate: isOrdersType ? prev.orderDate : false,
      deliveredOn: isOrdersType ? prev.deliveredOn : false,
      orderNumber: showsOrderNumber ? prev.orderNumber : false,
    }));
  }, [isOrdersType, showsOrderNumber]);

  const getValidationErrors = () => ({
    firstName: !firstName.trim(),
    lastName: !lastName.trim(),
    email: !isValidEmailAddress(email),
    orderNumber: showsOrderNumber ? !orderNumber.trim() : false,
    orderDate: isOrdersType ? !orderDate : false,
    deliveredOn: isOrdersType ? !deliveredOn : false,
  });

  const submitForm = async (payload) => {
    try {
      const response = await postToEventGrid(payload);
      setFormStatus(response.status === 200 ? "success" : "error");
    } catch (e) {
      setFormStatus("error");
    }
  };

  const handleSubmit = async () => {
    const validationErrorState = getValidationErrors();
    setValidationErrors(validationErrorState);
    const errorsPresent = Object.values(validationErrorState).some(Boolean);
    setHasErrors(errorsPresent);

    if (!errorsPresent) {
      const locale =
        window.asos.webContext && window.asos.webContext.getBrowseLanguage();
      const payload = {
        first_name: firstName,
        last_name: lastName,
        email,
        contract_type: contractType,
        order_number: orderNumber,
        order_date: orderDate,
        delivered_on: deliveredOn,
        locale,
      };
      await submitForm(payload);
    }
  };

  const onSubmit = async () => {
    setSubmitInProgress(true);
    await handleSubmit();
    setSubmitInProgress(false);
  };

  const link = (href, textKey) => (
    <a key={href} href={href} className={styles.inlineLink}>
      {translate(textKey)}
    </a>
  );

  const renderPreSubmitSection = () => (
    <div className={styles.preSubmitSection}>
      <p className="app_leavesden3">
        {translate("contract_withdrawal_form_confirm_acknowledgement")}
      </p>
      {isOrdersType && (
        <p className="app_leavesden3">
          {replaceJSX(
            replaceJSX(
              translate("contract_withdrawal_orders_form_confirm_para_2"),
              "%{tcsLink}",
              link(links.TCS, "contract_withdrawal_link_here")
            ),
            "%{returnsLink}",
            link(links.RETURNS, "contract_withdrawal_link_here")
          )}
        </p>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <span className={`${styles.requiredLabel} app_leavesden2`}>
          {translate("contract_withdrawal_form_required_information")}
        </span>

        <div className={styles.formControl}>
          <label htmlFor="firstName" className="app_london3">
            {translate("contract_withdrawal_form_first_name_label")}
          </label>
          <TextField
            id="firstName"
            ariaLabel={translate("contract_withdrawal_form_first_name_label")}
            initialValue={firstName}
            onChange={(e) => {
              dispatch(setFirstName(e.target.value));
              setValidationErrors((prev) => ({
                ...prev,
                firstName: !e.target.value.trim(),
              }));
            }}
            hasError={validationErrors.firstName}
            errorMessage={translate(
              "contract_withdrawal_form_first_name_error"
            )}
            placeholder={translate(
              "contract_withdrawal_form_first_name_placeholder"
            )}
            required
          />
        </div>

        <div className={styles.formControl}>
          <label htmlFor="lastName" className="app_london3">
            {translate("contract_withdrawal_form_last_name_label")}
          </label>
          <TextField
            id="lastName"
            ariaLabel={translate("contract_withdrawal_form_last_name_label")}
            initialValue={lastName}
            onChange={(e) => {
              dispatch(setLastName(e.target.value));
              setValidationErrors((prev) => ({
                ...prev,
                lastName: !e.target.value.trim(),
              }));
            }}
            hasError={validationErrors.lastName}
            errorMessage={translate("contract_withdrawal_form_last_name_error")}
            placeholder={translate(
              "contract_withdrawal_form_last_name_placeholder"
            )}
            required
          />
        </div>

        <div className={styles.formControl}>
          <label htmlFor="emailAddress" className="app_london3">
            {translate("contract_withdrawal_form_email_label")}
          </label>
          <TextField
            id="emailAddress"
            ariaLabel={translate("contract_withdrawal_form_email_label")}
            initialValue={email}
            onChange={(e) => {
              dispatch(setEmailAddress(e.target.value));
              setValidationErrors((prev) => ({
                ...prev,
                email: !isValidEmailAddress(e.target.value),
              }));
            }}
            hasError={validationErrors.email}
            errorMessage={translate("contract_withdrawal_form_email_error")}
            placeholder={translate(
              "contract_withdrawal_form_email_placeholder"
            )}
            required
            description={translate(
              "contract_withdrawal_form_email_description"
            )}
          />
        </div>

        {showsOrderNumber && (
          <div className={styles.formControl}>
            <label htmlFor="orderNumber" className="app_london3">
              {translate("contract_withdrawal_form_order_number_label")}
            </label>
            <TextField
              id="orderNumber"
              ariaLabel={translate(
                "contract_withdrawal_form_order_number_label"
              )}
              initialValue={orderNumber}
              onChange={(e) => {
                dispatch(setOrderNumber(e.target.value));
                setValidationErrors((prev) => ({
                  ...prev,
                  orderNumber: !e.target.value.trim(),
                }));
              }}
              hasError={validationErrors.orderNumber}
              errorMessage={translate(
                "contract_withdrawal_form_order_number_error"
              )}
              placeholder={translate(
                "contract_withdrawal_form_order_number_placeholder"
              )}
              required
              description={translate(
                ORDER_NUMBER_DESCRIPTION_KEYS[contractType]
              )}
            />
          </div>
        )}

        {isOrdersType && (
          <>
            <div className={styles.formControl}>
              <label htmlFor="orderDate" className="app_london3">
                {translate("contract_withdrawal_form_order_date_label")}
              </label>
              <TextField
                id="orderDate"
                ariaLabel={translate(
                  "contract_withdrawal_form_order_date_label"
                )}
                initialValue={orderDate}
                onChange={(e) => {
                  dispatch(setOrderDate(e.target.value));
                  setValidationErrors((prev) => ({
                    ...prev,
                    orderDate: !e.target.value,
                  }));
                }}
                hasError={validationErrors.orderDate}
                errorMessage={translate(
                  "contract_withdrawal_form_order_date_error"
                )}
                type="date"
                required
              />
            </div>

            <div className={styles.formControl}>
              <label htmlFor="deliveredOn" className="app_london3">
                {translate("contract_withdrawal_form_delivered_on_label")}
              </label>
              <TextField
                id="deliveredOn"
                ariaLabel={translate(
                  "contract_withdrawal_form_delivered_on_label"
                )}
                initialValue={deliveredOn}
                onChange={(e) => {
                  dispatch(setDeliveredOn(e.target.value));
                  setValidationErrors((prev) => ({
                    ...prev,
                    deliveredOn: !e.target.value,
                  }));
                }}
                hasError={validationErrors.deliveredOn}
                errorMessage={translate(
                  "contract_withdrawal_form_delivered_on_error"
                )}
                type="date"
                required
              />
            </div>
          </>
        )}

        {renderPreSubmitSection()}

        <p className={`${styles.privacyNotice} app_leavesden3`}>
          {replaceJSX(
            translate("contract_withdrawal_form_privacy_notice"),
            "%{privacyNoticeLink}",
            <a
              key="privacy-notice-link"
              href={links.PRIVACY_NOTICE}
              className={styles.inlineLink}
            >
              {translate("contract_withdrawal_form_privacy_notice_link_text")}
            </a>
          )}
        </p>

        <Button
          className="app_button"
          onClick={onSubmit}
          disabled={submitInProgress || hasErrors}
          isInProgress={submitInProgress}
        >
          {translate("contract_withdrawal_form_submit_cta")}
        </Button>
      </div>
    </div>
  );
}

WithdrawalForm.propTypes = {
  setFormStatus: PropTypes.func.isRequired,
};
